/* ===========================================================================
   ADVANCED BUNDLE BUILDER — runtime
   ---------------------------------------------------------------------------
   Vanilla ES6, sin dependencias. Arquitectura:

     AdvancedBundleElement  Custom element. Ciclo de vida = ciclo del DOM, así
                            que el Theme Editor (shopify:section:load/unload)
                            queda cubierto sin listeners extra.
     BundleManager          Orquesta la sección: delegación de eventos, carrito,
                            countdown, animaciones. API pública.
     BundleBlock            Un bloque = un bundle. Guarda config + estado y
                            delega el cálculo a su estrategia.
     Strategies             Un objeto por tipo de bundle, registrado en el
                            registro. Añadir un tipo NO requiere tocar nada más
                            (principio Open/Closed).
     CartBridge             Única puerta al carrito: AJAX Cart API + Section
                            Rendering API + compatibilidad con cart drawers.

   Eventos emitidos (burbujean hasta document):
     bundle:selected  { blockId, type, selection }
     bundle:updated   { blockId, type, totals }
     bundle:added     { blockId, type, items, cart }
     bundle:error     { blockId, type, error }
   =========================================================================== */

(function () {
  'use strict';

  // Si el archivo se incluye más de una vez en la misma página (dos bloques
  // que ambos referencian el asset, un merchant que lo importó a mano además
  // del bloque), la segunda ejecución sería un IIFE nuevo con su propio
  // `instances`/`types`, desconectado de los custom elements ya definidos por
  // la primera. Cortarlo aquí evita ese desajuste.
  if (window.__ADVANCED_BUNDLE_LOADED__) return;
  window.__ADVANCED_BUNDLE_LOADED__ = true;

  var VERSION = '1.0.0';

  /* =========================================================================
     1. UTILIDADES
     ========================================================================= */

  function $(selector, root) {
    return (root || document).querySelector(selector);
  }

  function $$(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  /** Lee y parsea un <script type="application/json"> hijo del elemento dado. */
  function readJSON(root, selector) {
    var el = $(selector, root);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error('[AdvancedBundle] JSON inválido en ' + selector, error);
      return null;
    }
  }

  /**
   * Formatea céntimos con el money_format de la tienda.
   * Implementación equivalente a Shopify.formatMoney para no depender de que
   * el tema cargue option_selection.js.
   */
  function formatMoney(cents, format) {
    var formatString = format || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = formatString.match(placeholder);
    if (!match) return formatString;

    var amount = typeof cents === 'string' ? parseFloat(cents.replace('.', '')) : cents;
    if (isNaN(amount) || amount === null) amount = 0;

    function withDelimiters(precision, thousands, decimal) {
      var number = (amount / 100.0).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(2, "'", '.');
        break;
      default:
        value = withDelimiters(2, ',', '.');
    }

    return formatString.replace(placeholder, value);
  }

  /** Sustituye el token [n] de los textos configurables por un número. */
  function interpolate(text, n) {
    return String(text || '').replace(/\[n\]/g, n);
  }

  /* --- Helpers de dominio (producto / precio) ------------------------------ */

  function findVariant(product, variantId) {
    if (!product || !product.variants || !product.variants.length) return null;
    var found = null;
    if (variantId) {
      found = product.variants.filter(function (v) {
        return String(v.id) === String(variantId);
      })[0];
    }
    if (found) return found;
    return (
      product.variants.filter(function (v) {
        return v.available;
      })[0] || product.variants[0]
    );
  }

  /** Precio de referencia: el compare_at si existe, si no el precio normal. */
  function refPrice(variant) {
    if (!variant) return 0;
    return variant.compare_at_price && variant.compare_at_price > variant.price
      ? variant.compare_at_price
      : variant.price;
  }

  function applyDiscount(cents, percent) {
    if (!percent) return cents;
    return Math.round((cents * (100 - percent)) / 100);
  }

  /* =========================================================================
     2. REGISTRO DE TIPOS (Open/Closed)
     -------------------------------------------------------------------------
     Una estrategia implementa, como mucho, estos métodos. Todos son
     opcionales: BASE_STRATEGY aporta el comportamiento por defecto.

       createState(block)              -> objeto de estado inicial
       computeTotals(block)            -> { total, compare, savings, unit, count, discount }
       getLineItems(block)             -> [{ id, quantity, properties }]
       validate(block)                 -> { valid, message }
       onAction(block, action, el, ev) -> true si consume el evento
       render(block, totals)           -> pintado específico del tipo
     ========================================================================= */

  var types = {};

  var BASE_STRATEGY = {
    createState: function () {
      return {};
    },
    computeTotals: function () {
      return { total: 0, compare: 0, savings: 0, unit: 0, count: 0, discount: 0 };
    },
    getLineItems: function () {
      return [];
    },
    validate: function () {
      return { valid: true, message: '' };
    },
    onAction: function () {
      return false;
    },
    render: function () {}
  };

  function registerType(name, strategy) {
    types[name] = Object.assign({}, BASE_STRATEGY, strategy || {});
    return types[name];
  }

  /* =========================================================================
     3. CART BRIDGE
     -------------------------------------------------------------------------
     Todo el contacto con Shopify pasa por aquí. Si mañana cambia la forma de
     refrescar el carrito, se cambia en un único sitio.
     ========================================================================= */

  var CartBridge = {
    /** Añade líneas al carrito. Devuelve la respuesta de /cart/add.js. */
    add: function (items, settings) {
      var body = { items: items };
      var sections = (settings.cartSections || '')
        .split(',')
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean);

      // La Section Rendering API devuelve el HTML ya renderizado en la misma
      // petición: un round-trip en lugar de dos.
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      return fetch(settings.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            var error = new Error(data.description || data.message || 'Cart error');
            error.data = data;
            throw error;
          }
          return data;
        });
      });
    },

    /** Estado actual del carrito. */
    get: function (settings) {
      return fetch(settings.routes.cart + '.js', {
        headers: { Accept: 'application/json' }
      }).then(function (r) {
        return r.json();
      });
    },

    /**
     * Aplica un código de descuento activando la cookie de descuento.
     * Es un fallback: lo robusto es un descuento automático (Shopify Functions).
     */
    applyDiscountCode: function (code, settings) {
      if (!code) return Promise.resolve();
      var url = (settings.routes.root || '/') + 'discount/' + encodeURIComponent(code);
      return fetch(url, { credentials: 'same-origin' }).catch(function () {
        /* Silencioso: un código inválido no debe romper el add-to-cart. */
      });
    },

    /** Inyecta el HTML devuelto por la Section Rendering API. */
    renderSections: function (sections) {
      if (!sections) return false;
      var rendered = false;

      Object.keys(sections).forEach(function (id) {
        var target = document.getElementById('shopify-section-' + id);
        if (!target || typeof sections[id] !== 'string') return;

        var parsed = new DOMParser().parseFromString(sections[id], 'text/html');
        var source = parsed.getElementById('shopify-section-' + id) || parsed.body;
        target.innerHTML = source.innerHTML;
        rendered = true;
      });

      return rendered;
    },

    /**
     * Notifica al tema que el carrito cambió. Cubre Dawn (pubsub + cart-drawer),
     * Prestige, Impulse, Shrine y temas que escuchan eventos genéricos.
     */
    notifyTheme: function (cart) {
      // Dawn y derivados: pub/sub interno.
      try {
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'advanced-bundle',
            cartData: cart
          });
        }
      } catch (e) {
        /* noop */
      }

      // Eventos genéricos que escuchan la mayoría de temas modernos.
      ['cart:refresh', 'cart:updated', 'cart:build', 'cart:change'].forEach(function (name) {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { cart: cart } }));
      });
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } })
      );
    },

    /** Abre el cart drawer del tema. Devuelve false si no encuentra ninguno. */
    openDrawer: function () {
      var drawer =
        $('cart-drawer') ||
        $('cart-notification') ||
        $('#CartDrawer') ||
        $('[data-cart-drawer]') ||
        $('.js-drawer--cart');

      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return true;
      }

      // Prestige / Impulse exponen su drawer en el objeto `theme`.
      try {
        if (window.theme && window.theme.cartDrawer && typeof window.theme.cartDrawer.open === 'function') {
          window.theme.cartDrawer.open();
          return true;
        }
      } catch (e) {
        /* noop */
      }

      if (drawer) {
        document.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
        drawer.classList.remove('is-empty');
        return true;
      }

      return false;
    }
  };

  /* =========================================================================
     4. BUNDLE BLOCK
     ========================================================================= */

  function BundleBlock(el, manager) {
    this.el = el;
    this.manager = manager;
    this.settings = manager.settings;
    this.id = el.dataset.blockId;
    this.type = el.dataset.type;

    this.config = readJSON(el, '[data-ab-config]') || { type: this.type, blockId: this.id };
    this.strategy = types[this.type] || BASE_STRATEGY;
    this.state = this.strategy.createState(this);

    // Cacheamos los nodos que se actualizan en cada render.
    this.nodes = {
      options: $$('[data-ab-options] [data-ab-card]', el),
      items: $$('[data-ab-items] [data-ab-item]', el),
      summary: $('[data-ab-summary]', el),
      button: $('[data-ab-action="add"]', el),
      buttonLabel: $('[data-ab-cta-label]', el),
      message: $('[data-ab-message]', el),
      progress: $('[data-ab-progress]', el)
    };
  }

  BundleBlock.prototype = {
    /* --- Lectura --------------------------------------------------------- */

    totals: function () {
      return this.strategy.computeTotals(this);
    },

    lineItems: function () {
      return this.strategy.getLineItems(this);
    },

    validate: function () {
      return this.strategy.validate(this);
    },

    /** Propiedades de línea que identifican el bundle en el carrito/pedido. */
    bundleProperties: function (extra) {
      var props = {
        _bundle_id: this.id,
        _bundle_type: this.type
      };
      if (extra) Object.assign(props, extra);
      return props;
    },

    /* --- Escritura ------------------------------------------------------- */

    /** Recalcula y repinta. Punto único de actualización visual. */
    update: function (silent) {
      var totals = this.totals();
      var validation = this.validate();

      this.renderTotals(totals);
      this.renderButton(validation);
      this.strategy.render(this, totals, validation);

      if (!silent) {
        this.manager.emit('bundle:updated', {
          blockId: this.id,
          type: this.type,
          totals: totals,
          valid: validation.valid
        });
      }

      return totals;
    },

    /** Pinta un grupo de precio (componente bundle-price). */
    renderPriceGroup: function (group, data) {
      if (!group) return;
      var money = this.manager.money.bind(this.manager);

      var finalEl = $('[data-ab-final]', group);
      if (finalEl && typeof data.total === 'number') finalEl.textContent = money(data.total);

      var compareEl = $('[data-ab-compare]', group);
      if (compareEl) {
        var showCompare = typeof data.compare === 'number' && data.compare > data.total;
        compareEl.textContent = showCompare ? money(data.compare) : '';
        compareEl.hidden = !showCompare;
      }

      var unitEl = $('[data-ab-unit]', group);
      if (unitEl && typeof data.unit === 'number') unitEl.textContent = money(data.unit);

      var savingsEl = $('[data-ab-savings]', group);
      var savingsWrap = $('[data-ab-savings-wrap]', group);
      if (savingsEl) savingsEl.textContent = money(data.savings || 0);
      if (savingsWrap) savingsWrap.hidden = !(data.savings > 0);
    },

    renderTotals: function (totals) {
      if (!this.nodes.summary) return;
      this.renderPriceGroup($('[data-ab-price-group="total"]', this.nodes.summary), totals);
    },

    renderButton: function (validation) {
      var button = this.nodes.button;
      if (!button) return;

      button.disabled = !validation.valid;

      if (this.nodes.buttonLabel && validation.label) {
        this.nodes.buttonLabel.textContent = validation.label;
      } else if (this.nodes.buttonLabel && this.config.cta) {
        this.nodes.buttonLabel.textContent = this.config.cta;
      }

      if (!validation.valid && validation.message) {
        this.setMessage(validation.message, 'warning');
      } else {
        this.clearMessage();
      }
    },

    setMessage: function (text, state) {
      var el = this.nodes.message;
      if (!el) return;
      el.textContent = text;
      el.dataset.state = state || 'warning';
      el.hidden = !text;
    },

    clearMessage: function () {
      var el = this.nodes.message;
      if (!el) return;
      el.textContent = '';
      el.hidden = true;
    },

    /**
     * Estado de carga del CTA.
     *
     * Al salir NO se llama a update(): eso repintaría el bloque y borraría el
     * mensaje de confirmación que acaba de escribir el flujo de carrito. Se
     * revalida solo el botón, que es lo único que puede haber cambiado.
     */
    setLoading: function (isLoading) {
      var button = this.nodes.button;
      if (!button) return;

      button.classList.toggle('is-loading', isLoading);
      button.disabled = isLoading ? true : !this.validate().valid;

      if (this.nodes.buttonLabel) {
        this.nodes.buttonLabel.textContent = isLoading
          ? this.settings.strings.adding
          : this.config.cta || this.settings.strings.addToCart;
      }
    },

    /** Marca visualmente la opción/tarjeta seleccionada. */
    selectOption: function (index) {
      this.state.index = index;

      this.nodes.options.forEach(function (card, i) {
        var selected = i === index;
        card.classList.toggle('is-selected', selected);
        var hit = $('[data-ab-action="select-option"]', card);
        if (hit) hit.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      this.manager.emit('bundle:selected', {
        blockId: this.id,
        type: this.type,
        index: index,
        selection: this.state
      });

      this.update();
    }
  };

  /* =========================================================================
     5. ESTRATEGIAS POR TIPO
     ========================================================================= */

  /* --- 1 y 6: Quantity Bundle / Family Pack --------------------------------
     `config.perUnitVariants` (opcional, default false) es lo único que separa
     dos modos de compra dentro de la MISMA estrategia:

       - false (comportamiento original, intacto): una sola variante para
         todas las unidades del tier. Lo usan quantity_bundle y family_pack
         dentro de la sección completa Advanced Bundle.
       - true: cada unidad del tier elige su propia variante (p. ej. "Compra 3"
         con un color distinto por unidad). Lo usa el bloque de main-product.

     Ningún bundle existente pasa este flag, así que su comportamiento no
     cambia una coma: es extensión pura, no modificación.
     --------------------------------------------------------------------- */

  var quantityStrategy = {
    createState: function (block) {
      var product = block.config.product;
      var state = {
        index: block.config.defaultIndex || 0,
        variantId: product ? product.default_variant_id : null
      };

      if (block.config.perUnitVariants) {
        state.units = quantityStrategy.buildUnits(block, state.index);
      }

      return state;
    },

    currentTier: function (block) {
      return (block.config.tiers || [])[block.state.index] || { quantity: 1, discount: 0 };
    },

    /**
     * Con qué variante nace una unidad nueva.
     *
     * Es la que el cliente tiene elegida ahora (`state.variantId`), no la que
     * traía la página. La diferencia se ve al pasar de un pack a otro: quien
     * elige crema arriba y luego pide dos unidades esperaba dos cremas, y con
     * `default_variant_id` la segunda nacía verde sin decir nada.
     *
     * `createState` llama aquí antes de que exista `block.state`, y por eso el
     * respaldo sigue siendo la variante por defecto — que en el primer pintado
     * ya es `selected_or_first_available_variant`, o sea la correcta.
     */
    fillVariantId: function (block) {
      var product = block.config.product;
      var current = block.state && block.state.variantId;
      return current || (product ? product.default_variant_id : null);
    },

    buildUnits: function (block, index) {
      var fillId = quantityStrategy.fillVariantId(block);
      var tier = (block.config.tiers || [])[index] || { quantity: 1 };
      var units = [];
      for (var i = 0; i < tier.quantity; i++) units.push({ variantId: fillId });
      return units;
    },

    /**
     * Ajusta block.state.units al tamaño del tier activo cada vez que se
     * consulta, conservando las elecciones ya hechas para las unidades que
     * siguen existiendo (comprar 2→3 no resetea las 2 primeras).
     */
    syncUnits: function (block) {
      if (!block.config.perUnitVariants) return;
      var tier = quantityStrategy.currentTier(block);
      var fillId = quantityStrategy.fillVariantId(block);
      var units = block.state.units || [];

      while (units.length < tier.quantity) units.push({ variantId: fillId });
      if (units.length > tier.quantity) units.length = tier.quantity;

      block.state.units = units;
    },

    computeTotals: function (block) {
      var tier = quantityStrategy.currentTier(block);

      if (block.config.perUnitVariants) {
        quantityStrategy.syncUnits(block);
        var gross = 0;
        var compare = 0;

        block.state.units.forEach(function (unit) {
          var variant = findVariant(block.config.product, unit.variantId);
          if (!variant) return;
          gross += variant.price;
          compare += refPrice(variant);
        });

        var totalPU = applyDiscount(gross, tier.discount);
        compare = Math.max(compare, gross);

        return {
          total: totalPU,
          compare: compare,
          savings: Math.max(0, compare - totalPU),
          unit: tier.quantity ? Math.round(totalPU / tier.quantity) : 0,
          count: tier.quantity,
          discount: tier.discount
        };
      }

      var variant = findVariant(block.config.product, block.state.variantId);
      if (!variant) return BASE_STRATEGY.computeTotals();

      var grossSV = variant.price * tier.quantity;
      var compareSV = Math.max(refPrice(variant) * tier.quantity, grossSV);
      var totalSV = applyDiscount(grossSV, tier.discount);

      return {
        total: totalSV,
        compare: compareSV,
        savings: Math.max(0, compareSV - totalSV),
        unit: Math.round(totalSV / tier.quantity),
        count: tier.quantity,
        discount: tier.discount
      };
    },

    getLineItems: function (block) {
      var tier = quantityStrategy.currentTier(block);

      if (block.config.perUnitVariants) {
        quantityStrategy.syncUnits(block);
        var groups = {};

        block.state.units.forEach(function (unit) {
          var variant = findVariant(block.config.product, unit.variantId);
          if (!variant || !variant.available) return;
          groups[variant.id] = (groups[variant.id] || 0) + 1;
        });

        return Object.keys(groups).map(function (id) {
          return {
            id: Number(id),
            quantity: groups[id],
            properties: block.bundleProperties({ _bundle_discount: String(tier.discount) })
          };
        });
      }

      var variant = findVariant(block.config.product, block.state.variantId);
      if (!variant) return [];

      return [
        {
          id: variant.id,
          quantity: tier.quantity,
          properties: block.bundleProperties({ _bundle_discount: String(tier.discount) })
        }
      ];
    },

    validate: function (block) {
      if (block.config.perUnitVariants) {
        quantityStrategy.syncUnits(block);
        var anyUnavailable = block.state.units.some(function (unit) {
          var variant = findVariant(block.config.product, unit.variantId);
          return !variant || !variant.available;
        });
        if (anyUnavailable || !block.state.units.length) {
          return { valid: false, message: block.settings.strings.soldOut };
        }
        return { valid: true, message: '' };
      }

      var variant = findVariant(block.config.product, block.state.variantId);
      if (!variant || !variant.available) {
        return { valid: false, message: block.settings.strings.soldOut };
      }
      return { valid: true, message: '' };
    },

    onAction: function (block, action, el) {
      if (action === 'set-unit-variant') {
        quantityStrategy.syncUnits(block);
        var index = parseInt(el.dataset.index, 10);
        if (block.state.units[index]) block.state.units[index].variantId = el.value;
        block.update();
        return true;
      }

      if (action !== 'set-main-variant') return false;
      block.state.variantId = el.value;
      block.update();
      return true;
    },

    render: function (block, totals) {
      quantityStrategy.relocatePicker(block);

      if (block.config.perUnitVariants) {
        quantityStrategy.renderUnits(block);
      }

      // Vista previa de cada tarjeta: en modo compartido usa la variante
      // elegida; en modo por-unidad usa la variante por defecto del producto,
      // porque el precio exacto de un tier no elegido depende de decisiones
      // que el cliente aún no ha tomado.
      var refVariantId = block.config.perUnitVariants
        ? block.config.product && block.config.product.default_variant_id
        : block.state.variantId;
      var refVariant = findVariant(block.config.product, refVariantId);
      if (!refVariant) return;

      (block.config.tiers || []).forEach(function (tier, i) {
        var card = block.nodes.options[i];
        if (!card) return;

        var gross = refVariant.price * tier.quantity;
        var compare = Math.max(refPrice(refVariant) * tier.quantity, gross);
        var total = applyDiscount(gross, tier.discount);

        block.renderPriceGroup($('[data-ab-price-group="card"]', card), {
          total: total,
          compare: compare,
          savings: Math.max(0, compare - total),
          unit: Math.round(total / tier.quantity)
        });
      });
    },

    /**
     * Mueve el picker de variantes (per-unit o compartido) a la tarjeta
     * actualmente seleccionada. bundle-card.liquid imprime un slot
     * [data-ab-card-units] vacío en TODAS las tarjetas del bloque, pero solo
     * existe UN picker real en el DOM: se reubica en vez de duplicarse, así
     * que el SSR ya lo coloca en la tarjeta por defecto y esto solo mueve
     * nodos cuando el cliente cambia de opción.
     */
    relocatePicker: function (block) {
      var picker = $('[data-ab-picker]', block.el);
      if (!picker) return;

      var card = block.nodes.options[block.state.index];
      var slot = card && $('[data-ab-card-units]', card);
      if (slot && picker.parentElement !== slot) slot.appendChild(picker);
    },

    /**
     * Sincroniza la lista de selectores "un producto por unidad". Solo
     * reconstruye el DOM cuando cambia el número de unidades (cambio de
     * tier); en un cambio de variante normal solo refresca precios, para no
     * perder el foco/estado nativo del <select> que el usuario acaba de usar.
     */
    renderUnits: function (block) {
      var container = $('[data-ab-units]', block.el);
      if (!container) return;

      var product = block.config.product;
      var existing = $$('.ab-unit', container);

      if (existing.length !== block.state.units.length) {
        container.innerHTML = '';
        block.state.units.forEach(function (unit, i) {
          container.appendChild(buildUnitNode(block, product, i, unit.variantId));
        });
        return;
      }

      block.state.units.forEach(function (unit, i) {
        var node = existing[i];
        var variant = findVariant(product, unit.variantId);
        if (!node || !variant) return;

        var select = $('select', node);
        if (select && select.value !== String(unit.variantId)) select.value = unit.variantId;

        var priceEl = $('[data-ab-unit-price]', node);
        if (priceEl) priceEl.textContent = block.manager.money(variant.price);

        updateUnitMedia(node, product, unit.variantId);
      });
    }
  };

  registerType('quantity_bundle', quantityStrategy);
  registerType('family_pack', quantityStrategy);

  /* --- 2 y 3: Frequently Bought Together / Complete Kit --------------------- */

  var itemListStrategy = {
    createState: function (block) {
      var config = block.config;
      return {
        items: (config.products || []).map(function (product, i) {
          var locked = !config.allowDeselect || (config.lockFirst && i === 0);
          return {
            selected: locked ? true : !!config.preselectAll,
            locked: locked,
            variantId: product.default_variant_id,
            quantity: 1
          };
        })
      };
    },

    /** Devuelve [{ item, product, variant }] de las líneas seleccionadas. */
    selected: function (block) {
      return block.state.items
        .map(function (item, i) {
          return { item: item, product: block.config.products[i], index: i };
        })
        .filter(function (entry) {
          if (!entry.item.selected || !entry.product) return false;
          var variant = findVariant(entry.product, entry.item.variantId);
          return !!(variant && variant.available);
        })
        .map(function (entry) {
          entry.variant = findVariant(entry.product, entry.item.variantId);
          return entry;
        });
    },

    computeTotals: function (block) {
      var discount = block.config.discount || 0;
      var gross = 0;
      var compare = 0;
      var count = 0;

      itemListStrategy.selected(block).forEach(function (entry) {
        var qty = entry.item.quantity;
        gross += entry.variant.price * qty;
        compare += refPrice(entry.variant) * qty;
        count += qty;
      });

      var total = applyDiscount(gross, discount);
      compare = Math.max(compare, gross);

      return {
        total: total,
        compare: compare,
        savings: Math.max(0, compare - total),
        unit: count ? Math.round(total / count) : 0,
        count: count,
        discount: discount
      };
    },

    getLineItems: function (block) {
      var discount = block.config.discount || 0;
      return itemListStrategy.selected(block).map(function (entry) {
        return {
          id: entry.variant.id,
          quantity: entry.item.quantity,
          properties: block.bundleProperties({ _bundle_discount: String(discount) })
        };
      });
    },

    validate: function (block) {
      var count = itemListStrategy.selected(block).length;
      var min = block.config.minItems || 1;
      if (count < min) {
        return { valid: false, message: block.settings.strings.selectAtLeast };
      }
      return { valid: true, message: '' };
    },

    onAction: function (block, action, el) {
      var index = parseInt(el.dataset.index, 10);
      var item = block.state.items[index];
      if (!item) return false;

      switch (action) {
        case 'toggle-item':
          if (item.locked) return true;
          item.selected = el.checked;
          break;
        case 'set-variant':
          item.variantId = el.value;
          break;
        case 'set-qty':
          item.quantity = Math.max(1, parseInt(el.value, 10) || 1);
          el.value = item.quantity;
          break;
        case 'qty-inc':
        case 'qty-dec':
          item.quantity = Math.max(1, item.quantity + (action === 'qty-inc' ? 1 : -1));
          break;
        default:
          return false;
      }

      block.update();
      return true;
    },

    render: function (block) {
      block.state.items.forEach(function (item, i) {
        var node = block.nodes.items[i];
        var product = block.config.products[i];
        if (!node || !product) return;

        node.classList.toggle('is-selected', item.selected);

        var variant = findVariant(product, item.variantId);
        if (!variant) return;

        // Precio de la línea.
        block.renderPriceGroup($('[data-ab-price-group="item"]', node), {
          total: variant.price * item.quantity,
          compare: refPrice(variant) * item.quantity
        });

        // Imagen y stock siguen a la variante elegida.
        var image = $('[data-ab-item-image]', node);
        if (image && variant.image) {
          image.src = variant.image;
          image.removeAttribute('srcset');
        }

        renderStock(block, node, variant);

        var input = $('.ab-qty__input', node);
        if (input && String(item.quantity) !== input.value) input.value = item.quantity;
      });
    }
  };

  registerType('frequently_bought', itemListStrategy);
  registerType('complete_kit', itemListStrategy);

  /* --- 4: Mix & Match ------------------------------------------------------ */

  var mixMatchStrategy = {
    createState: function (block) {
      return {
        items: (block.config.products || []).map(function (product) {
          return { variantId: product.default_variant_id, quantity: 0 };
        })
      };
    },

    count: function (block) {
      return block.state.items.reduce(function (sum, item) {
        return sum + item.quantity;
      }, 0);
    },

    /** Descuento del tramo más alto alcanzado. */
    discountFor: function (block, count) {
      return (block.config.tiers || []).reduce(function (best, tier) {
        return count >= tier.quantity && tier.discount > best ? tier.discount : best;
      }, 0);
    },

    computeTotals: function (block) {
      var count = mixMatchStrategy.count(block);
      var discount = mixMatchStrategy.discountFor(block, count);
      var gross = 0;
      var compare = 0;

      block.state.items.forEach(function (item, i) {
        if (!item.quantity) return;
        var variant = findVariant(block.config.products[i], item.variantId);
        if (!variant) return;
        gross += variant.price * item.quantity;
        compare += refPrice(variant) * item.quantity;
      });

      var total = applyDiscount(gross, discount);
      compare = Math.max(compare, gross);

      return {
        total: total,
        compare: compare,
        savings: Math.max(0, compare - total),
        unit: count ? Math.round(total / count) : 0,
        count: count,
        discount: discount
      };
    },

    getLineItems: function (block) {
      var count = mixMatchStrategy.count(block);
      var discount = mixMatchStrategy.discountFor(block, count);

      return block.state.items
        .map(function (item, i) {
          if (!item.quantity) return null;
          var variant = findVariant(block.config.products[i], item.variantId);
          if (!variant) return null;
          return {
            id: variant.id,
            quantity: item.quantity,
            properties: block.bundleProperties({ _bundle_discount: String(discount) })
          };
        })
        .filter(Boolean);
    },

    /**
     * Tope efectivo de unidades: en modo "cantidad exacta" el máximo es la
     * cantidad requerida, así el cliente no puede pasarse desde la UI.
     */
    maxFor: function (block) {
      if (block.config.exact) return block.config.required || 0;
      return block.config.maxQuantity || 0;
    },

    validate: function (block) {
      var count = mixMatchStrategy.count(block);
      var required = block.config.required || 1;
      var max = mixMatchStrategy.maxFor(block);

      if (count < required) {
        return {
          valid: false,
          message: '',
          label: interpolate(block.settings.strings.selectMore, required - count)
        };
      }
      // Red de seguridad: la UI ya impide pasarse del tope.
      if (max && count > max) {
        return { valid: false, message: '', label: block.config.cta };
      }
      return { valid: true, message: '', label: block.config.cta };
    },

    onAction: function (block, action, el) {
      var index = parseInt(el.dataset.index, 10);
      var item = block.state.items[index];
      if (!item) return false;

      var max = mixMatchStrategy.maxFor(block);
      var count = mixMatchStrategy.count(block);

      switch (action) {
        case 'set-variant':
          item.variantId = el.value;
          break;
        case 'set-qty':
          var requested = Math.max(0, parseInt(el.value, 10) || 0);
          // Recorta al tope contando el resto de líneas.
          if (max) requested = Math.min(requested, Math.max(0, max - (count - item.quantity)));
          item.quantity = requested;
          break;
        case 'qty-inc':
          if (max && count >= max) return true;
          item.quantity += 1;
          break;
        case 'qty-dec':
          item.quantity = Math.max(0, item.quantity - 1);
          break;
        default:
          return false;
      }

      block.update();
      return true;
    },

    render: function (block, totals) {
      var required = block.config.required || 1;
      var strings = block.settings.strings;
      var max = mixMatchStrategy.maxFor(block);
      var atMax = max > 0 && totals.count >= max;

      block.state.items.forEach(function (item, i) {
        var node = block.nodes.items[i];
        if (!node) return;

        node.classList.toggle('is-selected', item.quantity > 0);

        var input = $('.ab-qty__input', node);
        if (input && String(item.quantity) !== input.value) input.value = item.quantity;

        // Al llegar al tope, los "+" se deshabilitan en todas las líneas.
        var inc = $('[data-ab-action="qty-inc"]', node);
        if (inc) inc.disabled = atMax;

        var variant = findVariant(block.config.products[i], item.variantId);
        if (!variant) return;

        block.renderPriceGroup($('[data-ab-price-group="item"]', node), {
          total: variant.price,
          compare: refPrice(variant)
        });
        renderStock(block, node, variant);
      });

      renderProgress(block, totals.count, required, {
        complete: strings.progressComplete,
        pending: interpolate(strings.selectMore, Math.max(0, required - totals.count))
      });
    }
  };

  registerType('mix_match', mixMatchStrategy);

  /* --- 5: Gift Bundle ------------------------------------------------------ */

  var giftStrategy = {
    createState: function (block) {
      var product = block.config.product;
      return {
        quantity: 1,
        variantId: product ? product.default_variant_id : null
      };
    },

    unlocked: function (block) {
      return block.state.quantity >= (block.config.threshold || 1);
    },

    computeTotals: function (block) {
      var variant = findVariant(block.config.product, block.state.variantId);
      if (!variant) return BASE_STRATEGY.computeTotals();

      var qty = block.state.quantity;
      var discount = block.config.discount || 0;
      var gross = variant.price * qty;
      var total = applyDiscount(gross, discount);
      var compare = Math.max(refPrice(variant) * qty, gross);

      // El regalo suma al "precio de referencia" para que el ahorro mostrado
      // refleje su valor, pero nunca al total.
      if (giftStrategy.unlocked(block) && block.config.gift && block.config.giftFree) {
        var giftVariant = findVariant(block.config.gift, null);
        if (giftVariant) compare += giftVariant.price;
      }

      return {
        total: total,
        compare: compare,
        savings: Math.max(0, compare - total),
        unit: Math.round(total / qty),
        count: qty,
        discount: discount
      };
    },

    getLineItems: function (block) {
      var variant = findVariant(block.config.product, block.state.variantId);
      if (!variant) return [];

      var items = [
        {
          id: variant.id,
          quantity: block.state.quantity,
          properties: block.bundleProperties({ _bundle_discount: String(block.config.discount || 0) })
        }
      ];

      if (giftStrategy.unlocked(block) && block.config.gift) {
        var giftVariant = findVariant(block.config.gift, null);
        if (giftVariant && giftVariant.available) {
          var giftProps = {};
          giftProps[block.config.giftProperty || '_free_gift'] = 'true';
          items.push({
            id: giftVariant.id,
            quantity: 1,
            properties: block.bundleProperties(giftProps)
          });
        }
      }

      return items;
    },

    validate: function (block) {
      var variant = findVariant(block.config.product, block.state.variantId);
      if (!variant || !variant.available) {
        return { valid: false, message: block.settings.strings.soldOut };
      }
      return { valid: true, message: '' };
    },

    onAction: function (block, action, el) {
      // Solo la línea 0 (producto principal) es editable; el regalo no.
      var index = parseInt(el.dataset.index, 10);
      if (index !== 0) return true;

      switch (action) {
        case 'set-variant':
          block.state.variantId = el.value;
          break;
        case 'set-qty':
          block.state.quantity = Math.max(1, parseInt(el.value, 10) || 1);
          break;
        case 'qty-inc':
          block.state.quantity += 1;
          break;
        case 'qty-dec':
          block.state.quantity = Math.max(1, block.state.quantity - 1);
          break;
        default:
          return false;
      }

      block.update();
      return true;
    },

    render: function (block) {
      // Si el comerciante aún no ha elegido producto/regalo no hay markup que
      // actualizar: el bloque muestra el aviso de configuración.
      if (!block.nodes.items.length) return;

      var threshold = block.config.threshold || 1;
      var unlocked = giftStrategy.unlocked(block);
      var remaining = Math.max(0, threshold - block.state.quantity);

      var note = $('[data-ab-gift-note]', block.el);
      if (note) {
        note.textContent = unlocked
          ? block.config.unlockedText
          : interpolate(block.config.lockedText, remaining);
      }

      var giftNode = block.el.querySelector('[data-gift="true"]');
      if (giftNode) giftNode.classList.toggle('is-locked', !unlocked);

      var input = $('.ab-qty__input', block.nodes.items[0]);
      if (input && String(block.state.quantity) !== input.value) input.value = block.state.quantity;

      renderProgress(block, block.state.quantity, threshold, {
        complete: block.config.unlockedText,
        pending: interpolate(block.config.lockedText, remaining)
      });
    }
  };

  registerType('gift_bundle', giftStrategy);

  /* --- 7: Premium Bundle --------------------------------------------------- */

  var planStrategy = {
    createState: function (block) {
      return { index: block.config.defaultIndex || 0 };
    },

    currentPlan: function (block) {
      return (block.config.plans || [])[block.state.index] || { products: [], discount: 0 };
    },

    computeTotals: function (block) {
      var plan = planStrategy.currentPlan(block);
      var gross = 0;
      var compare = 0;

      plan.products.forEach(function (product) {
        var variant = findVariant(product, null);
        if (!variant) return;
        gross += variant.price;
        compare += refPrice(variant);
      });

      var total = applyDiscount(gross, plan.discount);
      compare = Math.max(compare, gross);

      return {
        total: total,
        compare: compare,
        savings: Math.max(0, compare - total),
        unit: plan.products.length ? Math.round(total / plan.products.length) : 0,
        count: plan.products.length,
        discount: plan.discount
      };
    },

    getLineItems: function (block) {
      var plan = planStrategy.currentPlan(block);
      return plan.products
        .map(function (product) {
          var variant = findVariant(product, null);
          if (!variant || !variant.available) return null;
          return {
            id: variant.id,
            quantity: 1,
            properties: block.bundleProperties({
              _bundle_discount: String(plan.discount),
              _bundle_plan: plan.name
            })
          };
        })
        .filter(Boolean);
    },

    validate: function (block) {
      var items = planStrategy.getLineItems(block);
      if (!items.length) {
        return { valid: false, message: block.settings.strings.soldOut };
      }
      return { valid: true, message: '' };
    }
  };

  registerType('premium_bundle', planStrategy);

  /* =========================================================================
     6. RENDERERS COMPARTIDOS
     -------------------------------------------------------------------------
     Los usan varias estrategias: viven fuera de ellas para no duplicarlos.
     ========================================================================= */

  /**
   * Construye el nodo "unidad N" de un quantity bundle en modo per-unit:
   * etiqueta + selector de variante + precio de esa unidad. Se crea con el
   * DOM API (no innerHTML) para no depender de escapar nada al interpolar.
   */
  function buildUnitNode(block, product, index, selectedVariantId) {
    var strings = block.settings.strings;

    var wrap = document.createElement('div');
    wrap.className = 'ab-unit';
    wrap.dataset.index = index;

    var media = document.createElement('span');
    media.className = 'ab-unit__media';
    var img = document.createElement('img');
    img.className = 'ab-unit__image';
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.loading = 'lazy';
    img.width = 36;
    img.height = 36;
    img.setAttribute('data-ab-unit-image', '');
    media.appendChild(img);
    wrap.appendChild(media);

    var select = document.createElement('select');
    select.className = 'ab-select ab-unit__select';
    select.setAttribute('data-ab-action', 'set-unit-variant');
    select.setAttribute('data-index', String(index));
    select.setAttribute('aria-label', strings.chooseVariant + ' — ' + interpolate(strings.itemLabel, index + 1));

    (product ? product.variants : []).forEach(function (variant) {
      var option = document.createElement('option');
      option.value = variant.id;
      option.textContent = variant.available
        ? variant.title
        : variant.title + ' — ' + strings.outOfStock;
      option.disabled = !variant.available;
      if (String(variant.id) === String(selectedVariantId)) option.selected = true;
      select.appendChild(option);
    });

    wrap.appendChild(select);

    var price = document.createElement('span');
    price.className = 'ab-unit__price';
    price.setAttribute('data-ab-unit-price', '');
    var initialVariant = findVariant(product, selectedVariantId);
    if (initialVariant) price.textContent = block.manager.money(initialVariant.price);
    wrap.appendChild(price);

    updateUnitMedia(wrap, product, selectedVariantId);

    return wrap;
  }

  /**
   * Aplica la imagen de una fila "unidad N": prioriza la imagen propia de la
   * variante elegida y cae a la imagen general del producto si esa variante
   * no tiene una propia, igual que el resto de vistas previas del bundle
   * (ver itemListStrategy.render). Sin imagen disponible, deja el hueco vacío
   * con el fondo neutro definido en CSS en vez de mostrar un icono roto.
   */
  function updateUnitMedia(node, product, variantId) {
    var img = $('[data-ab-unit-image]', node);
    if (!img) return;

    var variant = findVariant(product, variantId);
    var src = (variant && variant.image) || (product && product.image) || '';

    if (src) {
      img.src = src;
      img.removeAttribute('srcset');
      img.closest('.ab-unit__media').classList.remove('is-empty');
    } else {
      img.removeAttribute('src');
      img.closest('.ab-unit__media').classList.add('is-empty');
    }
  }

  function renderStock(block, node, variant) {
    var el = $('[data-ab-stock]', node);
    if (!el || !block.settings.showStock) return;

    var strings = block.settings.strings;
    var threshold = block.settings.lowStockThreshold;
    var state = 'in';
    var text = strings.inStock;

    if (!variant.available) {
      state = 'out';
      text = strings.outOfStock;
    } else if (variant.inventory !== null && variant.inventory > 0 && variant.inventory <= threshold) {
      state = 'low';
      text = strings.lowStock;
    }

    el.className = 'ab-item__stock ab-item__stock--' + state;
    el.textContent = text;
  }

  function renderProgress(block, current, required, texts) {
    var wrap = block.nodes.progress;
    if (!wrap) return;

    var ratio = required ? clamp(current / required, 0, 1) : 0;
    var complete = current >= required;

    wrap.dataset.complete = complete ? 'true' : 'false';

    var bar = $('[data-ab-progress-bar]', wrap);
    if (bar) bar.style.setProperty('--ab-progress', ratio);

    var track = $('[data-ab-progress-track]', wrap);
    if (track) track.setAttribute('aria-valuenow', String(current));

    var count = $('[data-ab-progress-count]', wrap);
    if (count) count.textContent = String(current);

    var text = $('[data-ab-progress-text]', wrap);
    if (text) text.textContent = complete ? texts.complete : texts.pending;
  }

  /* =========================================================================
     7. BUNDLE MANAGER (API pública)
     ========================================================================= */

  function BundleManager(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.settings = readJSON(root, '[data-ab-settings]') || {};
    this.blocks = [];
    this.blocksById = {};
    this.observers = [];
    this.timers = [];
    this.initialized = false;
  }

  BundleManager.prototype = {
    /* --- Ciclo de vida ---------------------------------------------------- */

    init: function () {
      if (this.initialized) return this;

      this.buildBlocks();
      this.bindEvents();
      this.setupTabs();
      this.setupReveal();
      this.setupCountdown();

      this.blocks.forEach(function (block) {
        block.update(true);
      });

      this.initialized = true;
      return this;
    },

    /** Libera todo: listeners, observers e intervalos. */
    destroy: function () {
      if (this.handlers) {
        var root = this.root;
        Object.keys(this.handlers).forEach(function (type) {
          root.removeEventListener(type, this.handlers[type]);
        }, this);
        this.handlers = null;
      }

      this.observers.forEach(function (observer) {
        observer.disconnect();
      });
      this.observers = [];

      this.timers.forEach(clearInterval);
      this.timers = [];

      this.blocks = [];
      this.blocksById = {};
      this.initialized = false;
    },

    buildBlocks: function () {
      var manager = this;
      this.blocks = $$('[data-ab-block]', this.root).map(function (el) {
        var block = new BundleBlock(el, manager);
        manager.blocksById[block.id] = block;
        return block;
      });
    },

    /* --- Delegación de eventos -------------------------------------------
       Tres listeners para toda la sección, sin importar cuántos bloques,
       tarjetas o productos haya. Nada de listeners por elemento.
       --------------------------------------------------------------------- */

    bindEvents: function () {
      var manager = this;

      this.handlers = {
        click: function (event) {
          var target = event.target.closest('[data-ab-action]');
          if (!target || !manager.root.contains(target)) return;

          // Solo las acciones que nacen de un click. `toggle-item`, `set-qty` y
          // `set-variant` llegan por `change`: filtrarlas aquí evita procesarlas
          // dos veces.
          var action = target.getAttribute('data-ab-action');
          if (['select-option', 'select-tab', 'qty-inc', 'qty-dec', 'add'].indexOf(action) === -1) return;

          event.preventDefault();
          manager.handleAction(action, target, event);
        },

        change: function (event) {
          var target = event.target.closest('[data-ab-action]');
          if (!target || !manager.root.contains(target)) return;
          manager.handleAction(target.getAttribute('data-ab-action'), target, event);
        },

        // Navegación por teclado del tablist (patrón ARIA), también delegada.
        keydown: function (event) {
          var offset = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
          if (!offset || !manager.tabs || !manager.tabs.length) return;

          var current = event.target.closest('[data-ab-action="select-tab"]');
          if (!current) return;

          event.preventDefault();
          var i = manager.tabs.indexOf(current);
          var next = manager.tabs[(i + offset + manager.tabs.length) % manager.tabs.length];
          next.focus();
          manager.selectTab(next.dataset.blockId);
        },

        // Los inputs numéricos se escriben carácter a carácter: debounce para
        // no recalcular en cada pulsación.
        input: debounce(function (event) {
          var target = event.target.closest('[data-ab-action="set-qty"]');
          if (!target || !manager.root.contains(target)) return;
          manager.handleAction('set-qty', target, event);
        }, 250)
      };

      Object.keys(this.handlers).forEach(function (type) {
        manager.root.addEventListener(type, manager.handlers[type]);
      });
    },

    /** Enruta una acción del DOM al bloque correspondiente. */
    handleAction: function (action, el, event) {
      if (action === 'select-tab') {
        this.selectTab(el.dataset.blockId);
        return;
      }

      var blockEl = el.closest('[data-ab-block]');
      if (!blockEl) return;

      var block = this.blocksById[blockEl.dataset.blockId];
      if (!block) return;

      if (action === 'add') {
        this.addBundleToCart(block.id);
        return;
      }

      if (action === 'select-option') {
        block.selectOption(parseInt(el.dataset.index, 10) || 0);
        return;
      }

      // El resto lo resuelve la estrategia del tipo.
      if (block.strategy.onAction(block, action, el, event)) return;

      // Fallback genérico: los steppers escriben en el input y re-disparan.
      if (action === 'qty-inc' || action === 'qty-dec') {
        var wrap = el.closest('[data-ab-qty]');
        var input = wrap && $('.ab-qty__input', wrap);
        if (input) {
          var min = parseInt(input.min, 10) || 0;
          var next = (parseInt(input.value, 10) || 0) + (action === 'qty-inc' ? 1 : -1);
          input.value = Math.max(min, next);
          this.handleAction('set-qty', input, event);
        }
      }
    },

    /* --- Tabs -------------------------------------------------------------- */

    setupTabs: function () {
      // La navegación por teclado se resuelve en el handler delegado `keydown`.
      this.tabs = $$('[data-ab-action="select-tab"]', this.root);
    },

    selectTab: function (blockId) {
      if (!this.tabs || !this.tabs.length) return;

      this.tabs.forEach(function (tab) {
        var active = tab.dataset.blockId === blockId;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });

      $$('[data-ab-block]', this.root).forEach(function (panel) {
        var active = panel.dataset.blockId === blockId;
        panel.classList.toggle('is-hidden', !active);
        panel.hidden = !active;
      });
    },

    /* --- Animación de entrada ---------------------------------------------- */

    setupReveal: function () {
      // Sin IntersectionObserver no hay forma barata de saber cuándo revelar:
      // se apaga la animación y el contenido queda visible.
      if (!this.settings.animate || !('IntersectionObserver' in window)) {
        this.root.dataset.animate = 'off';
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry, i) {
            if (!entry.isIntersecting) return;
            entry.target.style.animationDelay = i * 60 + 'ms';
            entry.target.classList.add('ab-reveal');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
      );

      $$('.ab-card, .ab-item', this.root).forEach(function (el) {
        observer.observe(el);
      });

      this.observers.push(observer);
    },

    /* --- Countdown de urgencia ---------------------------------------------- */

    setupCountdown: function () {
      var config = this.settings.urgency;
      var wrap = $('[data-ab-urgency]', this.root);
      if (!config || !config.enabled || !wrap) return;

      var deadline = this.resolveDeadline(config);
      if (!deadline) return;

      wrap.hidden = false;

      var nodes = {
        hours: $('[data-ab-cd-hours]', wrap),
        minutes: $('[data-ab-cd-minutes]', wrap),
        seconds: $('[data-ab-cd-seconds]', wrap),
        text: $('[data-ab-urgency-text]', wrap)
      };

      function pad(n) {
        return String(n).padStart(2, '0');
      }

      function tick() {
        var remaining = deadline - Date.now();

        if (remaining <= 0) {
          wrap.dataset.expired = 'true';
          if (nodes.text) nodes.text.textContent = config.expiredText;
          clearInterval(interval);
          return;
        }

        var seconds = Math.floor(remaining / 1000);
        if (nodes.hours) nodes.hours.textContent = pad(Math.floor(seconds / 3600));
        if (nodes.minutes) nodes.minutes.textContent = pad(Math.floor((seconds % 3600) / 60));
        if (nodes.seconds) nodes.seconds.textContent = pad(seconds % 60);
      }

      tick();
      var interval = setInterval(tick, 1000);
      this.timers.push(interval);
    },

    /** Fecha límite absoluta, ya sea fija o por duración persistida. */
    resolveDeadline: function (config) {
      if (config.mode === 'fixed_date') {
        var parsed = new Date(config.date + 'T' + (config.time || '23:59') + ':00').getTime();
        return isNaN(parsed) ? null : parsed;
      }

      var durationMs = (config.hours || 24) * 3600 * 1000;
      var stored = null;

      try {
        stored = window.localStorage.getItem(config.storageKey);
      } catch (e) {
        /* Modo incógnito o storage bloqueado: el contador vive solo en memoria. */
      }

      if (stored && parseInt(stored, 10) > Date.now()) return parseInt(stored, 10);

      var deadline = Date.now() + durationMs;
      try {
        window.localStorage.setItem(config.storageKey, String(deadline));
      } catch (e) {
        /* noop */
      }
      return deadline;
    },

    /* --- API pública -------------------------------------------------------- */

    /** Bloque por id, o el primero si no se pasa id. */
    getBlock: function (blockId) {
      return blockId ? this.blocksById[blockId] : this.blocks[0];
    },

    /** Selecciona una opción/plan por índice. */
    selectBundle: function (blockId, index) {
      var block = this.getBlock(blockId);
      if (block) block.selectOption(index);
      return this;
    },

    /** Fuerza el recálculo de precios de uno o todos los bloques. */
    updatePrice: function (blockId) {
      if (blockId) {
        var block = this.getBlock(blockId);
        return block ? block.update() : null;
      }
      return this.blocks.map(function (b) {
        return b.update();
      });
    },

    /** Formatea céntimos con el money_format de la tienda. */
    money: function (cents) {
      return formatMoney(cents, this.settings.moneyFormat);
    },

    /** Añade el bundle indicado al carrito. Devuelve una promesa. */
    addBundleToCart: function (blockId) {
      var manager = this;
      var block = this.getBlock(blockId);
      if (!block) return Promise.reject(new Error('Bloque no encontrado'));

      var validation = block.validate();
      if (!validation.valid) {
        block.setMessage(validation.message || manager.settings.strings.error, 'warning');
        return Promise.reject(new Error(validation.message || 'invalid'));
      }

      var items = block.lineItems();
      if (!items.length) {
        block.setMessage(manager.settings.strings.selectAtLeast, 'warning');
        return Promise.reject(new Error('empty'));
      }

      block.setLoading(true);
      block.clearMessage();

      return CartBridge.add(items, this.settings)
        .then(function (response) {
          // El descuento por código es un fallback; con Shopify Functions el
          // descuento ya viaja aplicado y este paso no hace nada.
          if (manager.settings.discountStrategy === 'discount_code') {
            return CartBridge.applyDiscountCode(manager.settings.discountCode, manager.settings).then(
              function () {
                return response;
              }
            );
          }
          return response;
        })
        .then(function (response) {
          CartBridge.renderSections(response.sections);
          return CartBridge.get(manager.settings).then(function (cart) {
            CartBridge.notifyTheme(cart);
            manager.afterAdd(block);
            manager.emit('bundle:added', {
              blockId: block.id,
              type: block.type,
              items: items,
              cart: cart
            });
            return cart;
          });
        })
        .catch(function (error) {
          block.setMessage(error.message || manager.settings.strings.error, 'error');
          manager.emit('bundle:error', { blockId: block.id, type: block.type, error: error });
          throw error;
        })
        .finally(function () {
          block.setLoading(false);
        });
    },

    /** Qué hacer tras un add-to-cart correcto (configurable en el editor). */
    afterAdd: function (block) {
      var behavior = this.settings.cartBehavior;
      var routes = this.settings.routes;

      if (behavior === 'checkout') {
        window.location.href = '/checkout';
        return;
      }
      if (behavior === 'cart') {
        window.location.href = routes.cart;
        return;
      }

      block.setMessage(this.settings.strings.added, 'success');

      if (behavior === 'drawer' && !CartBridge.openDrawer()) {
        // El tema no tiene drawer: al menos confirmamos visualmente.
        block.nodes.button && block.nodes.button.classList.add('is-success');
        setTimeout(function () {
          block.nodes.button && block.nodes.button.classList.remove('is-success');
        }, 1600);
      }
    },

    /** Vuelve a pedir el carrito y notifica al tema. */
    refreshCart: function () {
      var manager = this;
      return CartBridge.get(this.settings).then(function (cart) {
        CartBridge.notifyTheme(cart);
        return cart;
      });
    },

    /** Emite un evento personalizado que burbujea hasta document. */
    emit: function (name, detail) {
      this.root.dispatchEvent(
        new CustomEvent(name, { bubbles: true, composed: true, detail: detail })
      );
    }
  };

  /* =========================================================================
     8. CUSTOM ELEMENT
     -------------------------------------------------------------------------
     connected/disconnectedCallback cubren el Theme Editor sin listeners de
     shopify:section:load, y garantizan que nunca queden listeners duplicados.
     ========================================================================= */

  var instances = [];

  class AdvancedBundleElement extends HTMLElement {
    connectedCallback() {
      if (this.manager) return;
      this.manager = new BundleManager(this).init();
      instances.push(this.manager);
    }

    disconnectedCallback() {
      if (!this.manager) return;
      this.manager.destroy();
      var i = instances.indexOf(this.manager);
      if (i > -1) instances.splice(i, 1);
      this.manager = null;
    }
  }

  if (!customElements.get('advanced-bundle')) {
    customElements.define('advanced-bundle', AdvancedBundleElement);
  }

  /* =========================================================================
     8b. SINCRONÍA CON EL PICKER DE VARIANTES DEL TEMA
     -------------------------------------------------------------------------
     El picker de la ficha y el bundle son dos controles del mismo producto, y
     hasta hoy no se hablaban: elegir un color arriba cambiaba la URL, la
     galería y el formulario del tema, pero el bundle seguía mandando al
     carrito la variante con la que se sirvió la página. En servidor no había
     fallo —`bundle-product-data` usa `selected_or_first_available_variant`—,
     así que el desajuste solo aparecía al cambiar de variante sin recargar.

     Se escucha el `change` delegado y NO el evento publicado `variant-change`
     del tema, y las tres razones están medidas en la tienda publicada:

       - el `change` llega a los 9 ms y el evento del tema a los 241, porque
         se publica después de una petición de red. Esos 232 ms son una
         ventana con el DOM diciendo un color y el motor diciendo otro, que es
         el mismo fallo en pequeño;
       - si esa petición falla, el evento no llega nunca y el bundle se queda
         desincronizado justo cuando peor viene;
       - y el bus del tema solo es medio alcanzable desde aquí: `subscribe` y
         `publish` son declaraciones de función de `pubsub.js` y sí están en
         `window`, pero `PUB_SUB_EVENTS` es un `const` de `constants.js`, y un
         `const` de nivel superior no se cuelga de `window`.

     El orden de burbujeo es lo que hace que esto funcione: `VariantSelects`
     escucha en el propio elemento, así que cuando el evento llega a
     `document` su `currentVariant` ya está recalculado.
     ========================================================================= */

  /**
   * Pertenencia estricta de una variante a un producto.
   *
   * No se usa `findVariant` a propósito: cuando no encuentra el id, cae a la
   * primera variante disponible en vez de devolver null, así que como test de
   * «¿es mi producto?» haría que cualquier bloque de la página adoptara
   * variantes ajenas.
   */
  function ownsVariant(product, variantId) {
    if (!product || !product.variants || !variantId) return false;
    return product.variants.some(function (v) {
      return String(v.id) === String(variantId);
    });
  }

  /**
   * Lleva al bloque la variante que el cliente ha elegido fuera de él.
   *
   * En modo por-unidad solo se mueven las unidades que seguían en la variante
   * anterior: quien puso a mano una crema y una verde no quiere que el picker
   * de arriba le borre la elección.
   *
   * Devuelve false si no había nada que hacer, para no repintar de balde.
   */
  function adoptVariant(block, variantId) {
    if (!block || !block.state || !block.config) return false;
    if (!ownsVariant(block.config.product, variantId)) return false;

    var previous = block.state.variantId;
    if (String(previous) === String(variantId)) return false;

    block.state.variantId = variantId;

    if (block.config.perUnitVariants && block.state.units) {
      block.state.units.forEach(function (unit) {
        if (String(unit.variantId) === String(previous)) unit.variantId = variantId;
      });
    }

    block.update();
    return true;
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var picker = target.closest('variant-selects');
    if (!picker) return;

    // Sin `currentVariant` la combinación no existe: el tema ya ha
    // deshabilitado la compra y no hay ninguna variante que adoptar.
    var variant = picker.currentVariant;
    if (!variant || !variant.id) return;

    instances.forEach(function (manager) {
      (manager.blocks || []).forEach(function (block) {
        adoptVariant(block, variant.id);
      });
    });
  });

  /* =========================================================================
     9. API GLOBAL
     -------------------------------------------------------------------------
     Ejemplos:
       AdvancedBundle.get('template--123__advanced_bundle').selectBundle(null, 2)
       AdvancedBundle.registerType('subscription_bundle', { ... })
       document.addEventListener('bundle:added', e => console.log(e.detail.cart))
     ========================================================================= */

  window.AdvancedBundle = {
    version: VERSION,
    registerType: registerType,
    types: types,
    BundleManager: BundleManager,
    BundleBlock: BundleBlock,
    CartBridge: CartBridge,
    formatMoney: formatMoney,
    instances: instances,
    helpers: {
      findVariant: findVariant,
      refPrice: refPrice,
      applyDiscount: applyDiscount,
      interpolate: interpolate,
      renderProgress: renderProgress,
      renderStock: renderStock
    },
    /** Devuelve el manager de una sección concreta (o el primero). */
    get: function (sectionId) {
      if (!sectionId) return instances[0];
      return instances.filter(function (manager) {
        return manager.sectionId === sectionId;
      })[0];
    }
  };
})();
